import { PCIDataSharedWithPartnersTabType } from './pciDashboard/widgets/pciDataSharedWithPartners/pciDataSharedWithPartners'
import { gql } from 'graphql-request'

export interface PCIFileData {
  id: string
  name: string
  instancesInFilesCount: number
  datasources: Array<{
    id: string
    name: string
    type: string
    objectsCount: number
  }>
}

export interface PCIDatabaseData {
  id: string
  name: string
  blobInstancesCount: number
  databaseStats: {
    totalTablesCount: number
    totalColumnsCount: number
    totalDatabasesCount: number
    databases: Array<{
      id: string
      name: string
      columnsCount: number
      datasourceId?: string
      datasourceType?: string
    }>
  }
}

export interface PCISharedWithPartnersData {
  id: string
  name: string
  partnersCount: number
  entitiesCountSharedWithPartners: number
  objectsSharedWithPartnerCount: number
  partners: Array<{
    id: string
    name: string
    objectsCount: number
  }>
  dataShareWithPartnersByDatasource: Array<{
    datasourceName: string
    datasourceType: string
    datasourceId: string
    objectsCount: number
  }>
}

export interface PCIAccessRelatedData {
  id: string
  name: string
  openAccessObjectsCount: number
  excessAccessObjectsCount: number
  externallySharedCount: number
  datasourcesWithOpenAccessCount: Array<{
    datasourceName: string
    datasourceType: string
    datasourceId: string
    objectsCount: number
  }>
  datasourcesWithExcessAccessCount: Array<{
    datasourceName: string
    datasourceType: string
    datasourceId: string
    objectsCount: number
  }>
}

export interface PCIFileDistributionLabel {
  id: string
  name: string
  objectCount: number
  type: string
  datasourceCounts: Array<{
    datasourceId: string
  }>
}

export interface PCIFileDistributionData {
  totalFiles: number
  datasourcesCount: number
  widgetData: Array<{
    id: string
    name: string
    objectCount: number
  }>
}
export interface FilesWithPCIDataParams {
  attributeSetId: string
}

export interface DatabasesWithPCIDataParams {
  attributeSetId: string
}

export interface PCIDataSharedWithPartnersParams {
  attributeSetId: string
  activeTab: PCIDataSharedWithPartnersTabType
}

export interface AccessRelatedDataForPCIParams {
  attributeSetId: string
}

export interface FileDistributionByPCILabelsParams {
  attributeSetName: string
}

export const queryFilesWithPCIData = (params: FilesWithPCIDataParams) => {
  return gql`
    {
      attributeSet(id: "${params.attributeSetId}") {
        edges {
          node {
            id
            name
            instancesInFilesCount
            datasources {
              count
              edges {
                node {
                  id
                  name
                  type
                  objects {
                    count
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryFilesWithPCIData = (raw: any): PCIFileData | null => {
  try {
    const node = raw?.attributeSet?.edges?.[0]?.node
    if (!node) return null

    return {
      id: node.id,
      name: node.name,
      instancesInFilesCount: node.instancesInFilesCount || 0,
      datasources:
        node.datasources?.edges?.map((edge: any) => ({
          id: edge.node.id,
          name: edge.node.name,
          type: edge.node.type,
          objectsCount: edge.node.objects?.count || 0
        })) || []
    }
  } catch (error) {
    console.error('Error mapping PCI files data:', error)
    throw error
  }
}

export const queryDatabasesWithPCIData = (params: DatabasesWithPCIDataParams) => {
  return gql`
    {
      attributeSet(id: "${params.attributeSetId}") {
        edges {
          node {
            id
            name
            blobInstancesCount
            databaseStats {
              totalTablesCount
              totalColumnsCount
              totalDatabasesCount
              databases {
                edges {
                  node {
                    datasource {
                      edges {
                        node {
                          id
                          type
                        }
                      }
                    }
                    id
                    name
                    columns {
                      count
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryDatabasesWithPCIData = (raw: any): PCIDatabaseData | null => {
  try {
    const node = raw?.attributeSet?.edges?.[0]?.node
    if (!node) return null

    return {
      id: node.id,
      name: node.name,
      blobInstancesCount: node.blobInstancesCount || 0,
      databaseStats: {
        totalTablesCount: node.databaseStats?.totalTablesCount || 0,
        totalColumnsCount: node.databaseStats?.totalColumnsCount || 0,
        totalDatabasesCount: node.databaseStats?.totalDatabasesCount || 0,
        databases:
          node.databaseStats?.databases?.edges?.map((edge: any) => {
            const datasource = edge.node.datasource?.edges?.[0]?.node
            return {
              id: edge.node.id,
              name: edge.node.name,
              columnsCount: edge.node.columns?.count || 0,
              datasourceId: datasource?.id,
              datasourceType: datasource?.type
            }
          }) || []
      }
    }
  } catch (error) {
    console.error('Error mapping PCI databases data:', error)
    throw error
  }
}

export const queryPCIDataSharedWithPartners = (params: { attributeSetId: string }) => {
  return gql`
    query {
      attributeSet(id: "${params.attributeSetId}") {
        edges {
          node {
            id
            name
            partnersCount
            entitiesCountSharedWithPartners
            objectsSharedWithPartnerCount
            partners(first: 5) {
              edges {
                node {
                  id
                  name
                  objects {
                    count
                  }
                }
              }
            }
            dataShareWithPartnersByDatasource {
              datasourceName
              datasourceType
              datasourceId
              objectsCount
            }
          }
        }
      }
    }
  `
}
export const mapQueryPCIDataSharedWithPartners = (raw: any): PCISharedWithPartnersData | null => {
  try {
    const node = raw?.attributeSet?.edges?.[0]?.node
    if (!node) return null

    return {
      id: node.id,
      name: node.name,
      partnersCount: node.partnersCount || 0,
      entitiesCountSharedWithPartners: node.entitiesCountSharedWithPartners || 0,
      objectsSharedWithPartnerCount: node.objectsSharedWithPartnerCount || 0,
      partners:
        node.partners?.edges?.map((edge: any) => ({
          id: edge.node.id,
          name: edge.node.name,
          objectsCount: edge.node.objects?.count || 0
        })) || [],
      dataShareWithPartnersByDatasource: node.dataShareWithPartnersByDatasource || []
    }
  } catch (error) {
    console.error('Error mapping PCI shared with partners data:', error)
    throw error
  }
}

export const queryAccessRelatedDataForPCI = (params: AccessRelatedDataForPCIParams) => {
  return gql`
    query {
      attributeSet(id: "${params.attributeSetId}") {
        edges {
          node {
            id
            name
            openAccessObjectsCount
            excessAccessObjectsCount
            externallySharedCount
            datasourcesWithOpenAccessCount {
              datasourceName
              datasourceType
              datasourceId
              objectsCount
            }
            datasourcesWithExcessAccessCount {
              datasourceName
              datasourceType
              datasourceId
              objectsCount
            }
          }
        }
      }
    }
  `
}

export const mapQueryAccessRelatedDataForPCI = (raw: any): PCIAccessRelatedData | null => {
  try {
    const node = raw?.attributeSet?.edges?.[0]?.node
    if (!node) return null

    return {
      id: node.id,
      name: node.name,
      openAccessObjectsCount: node.openAccessObjectsCount || 0,
      excessAccessObjectsCount: node.excessAccessObjectsCount || 0,
      externallySharedCount: node.externallySharedCount || 0,
      datasourcesWithOpenAccessCount: node.datasourcesWithOpenAccessCount || [],
      datasourcesWithExcessAccessCount: node.datasourcesWithExcessAccessCount || []
    }
  } catch (error) {
    console.error('Error mapping PCI access related data:', error)
    throw error
  }
}

export const queryFileDistributionByPCILabels = ({
  attributeSetName
}: FileDistributionByPCILabelsParams) => {
  return gql`
    {
      documentLabelSet(filter: [{ key: NAME, values: ["${attributeSetName}"] }]) {
        edges {
          node {
            id
            name
            description
            type
            labels {
              edges {
                node {
                  id
                  name
                  objectCount
                  type
                  datasourceCounts {
                    datasource {
                      edges {
                        node {
                          id
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryFileDistributionByPCILabels = (raw: any): PCIFileDistributionData | null => {
  try {
    const node = raw?.documentLabelSet?.edges?.[0]?.node
    if (!node) return null

    const labels =
      node.labels?.edges?.map((edge: any) => ({
        id: edge.node.id,
        name: edge.node.name,
        objectCount: edge.node.objectCount || 0,
        type: edge.node.type,
        datasourceCounts: edge.node.datasourceCounts || []
      })) || []

    const totalFiles = labels.reduce((sum, label) => sum + (label.objectCount || 0), 0)

    const uniqueDatasourceIds = new Set()
    labels.forEach((label) => {
      label.datasourceCounts?.forEach((datasourceCount: any) => {
        datasourceCount.datasource?.edges?.forEach((edge: any) => {
          if (edge.node?.id) {
            uniqueDatasourceIds.add(edge.node.id)
          }
        })
      })
    })

    return {
      totalFiles,
      datasourcesCount: uniqueDatasourceIds.size,
      widgetData: labels.map((label) => ({
        id: label.id,
        name: label.name,
        objectCount: label.objectCount
      }))
    }
  } catch (error) {
    console.error('Error mapping PCI file distribution data:', error)
    throw error
  }
}

export interface PCIAccessControlData {
  usersCount: number
  groupsCount: number
  openAccessObjectsCount: number
  excessAccessObjectsCount: number
  externallySharedCount: number
}

export interface AccessControlDataParams {
  attributeSetId: string
}

export const queryAccessControlData = (params: AccessControlDataParams) => {
  return gql`
    query {
      accessControlUsers(attributeSetIds: ["${params.attributeSetId}"]) {
        count
      }
      accessControlGroups(attributeSetIds: ["${params.attributeSetId}"]) {
        count
      }
      attributeSet(id: "${params.attributeSetId}") {
        edges {
          node {
            id
            name
            openAccessObjectsCount
            excessAccessObjectsCount
            externallySharedCount
          }
        }
      }
    }
  `
}

export const mapQueryAccessControlData = (raw: any): PCIAccessControlData => {
  try {
    return {
      usersCount: raw?.accessControlUsers?.count || 0,
      groupsCount: raw?.accessControlGroups?.count || 0,
      openAccessObjectsCount: raw?.attributeSet?.edges?.[0]?.node?.openAccessObjectsCount || 0,
      excessAccessObjectsCount: raw?.attributeSet?.edges?.[0]?.node?.excessAccessObjectsCount || 0,
      externallySharedCount: raw?.attributeSet?.edges?.[0]?.node?.externallySharedCount || 0
    }
  } catch (error) {
    console.error('Error mapping PCI access control data:', error)
    throw error
  }
}

export interface AttributeSetData {
  id: string
  name: string
  enabled: boolean
}

export const queryAttributeSetId = (attributeSetName: string) => {
  return gql`
    {
      attributeSet(filter: [{key: NAME, values: ["${attributeSetName}"]}]) {
        edges {
          node {
            id
            name
            enabled
          }
        }
      }
    }
  `
}

export const mapQueryAttributeSetId = (raw: any): AttributeSetData | null => {
  try {
    const node = raw?.attributeSet?.edges?.[0]?.node
    if (!node) return null

    return {
      id: node.id,
      name: node.name,
      enabled: node.enabled
    }
  } catch (error) {
    console.error('Error mapping attribute set data:', error)
    throw error
  }
}
